'use client';

import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import type { Document } from '@contentful/rich-text-types';
import { RenderRTE } from '@/components-2.0/structure/RenderRTE';
import { Recirculation } from '@/components/molecules/Recirculation';
import { useViewerTimeZone } from '@/hooks/useViewerTimeZone';
import { buildScheduleDays, upcomingItems, type ScheduleItem } from '@/packages/sxm/sports/scheduleItems';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import NoGames from './NoGames';
import { ScheduleList } from './ScheduleList';
import { ScheduleReminder, type ScheduleReminderProps } from './ScheduleReminder';
import styles from './sportschedule.module.scss';

const PAGE_SIZE = 6;

export interface TeamScheduleProps {
    items: ScheduleItem[];
    renderedAt: number;
    offSeason: { logo?: AEMImage; title?: string; description?: Document };
    reminder: ScheduleReminderProps;
}

export default function TeamSchedule({ items, renderedAt, offSeason, reminder }: TeamScheduleProps) {
    const timeZone = useViewerTimeZone();
    const [showAll, setShowAll] = useState(false);
    const now = useMemo(() => dayjs(renderedAt).tz(timeZone), [renderedAt, timeZone]);
    const upcoming = useMemo(() => upcomingItems(items, now, timeZone), [items, now, timeZone]);

    if (!items.length) {
        return (
            <Grid container>
                <Grid col={{ sm: 12, lg: 10 }} start={{ lg: 2 }}>
                    <NoGames logo={offSeason.logo} title={offSeason.title || ''}>
                        <RenderRTE className={styles.noGamesMsg} contentfulDoc={offSeason.description} />
                    </NoGames>
                </Grid>
            </Grid>
        );
    }

    const hasMore = !showAll && upcoming.length > PAGE_SIZE;
    let visible = upcoming;
    if (hasMore) visible = upcoming.slice(0, PAGE_SIZE);

    return (
        <Grid container>
            <Grid col={{ sm: 12, lg: 10 }} start={{ lg: 2 }}>
                <ScheduleList days={buildScheduleDays(visible, { now, timeZone })} timeZone={timeZone} />
                <LoadMore hasMore={hasMore} onLoadMore={() => setShowAll(true)} />
                <ScheduleReminder {...reminder} />
            </Grid>
        </Grid>
    );
}

function LoadMore({ hasMore, onLoadMore }: { hasMore: boolean; onLoadMore: () => void }) {
    if (!hasMore) {
        return (
            <Recirculation className={styles.allGamesMessage}>
                All available games have been loaded. Check back soon, we&apos;re still adding more.
            </Recirculation>
        );
    }

    return (
        <button type="button" className={styles.loadMoreGames} onClick={onLoadMore}>
            <span className={styles.loadMoreMessage}>Load More Games</span>
        </button>
    );
}
